// `/admin/kyc` (`spec/17-frontend.md` §8.2). Foco declarado: **a linha mais antiga da
// fila**, ela ganha painel próprio com o tempo de espera como número-herói.
//
// Esta é a rota-PAI: ela desenha a fila e, por cima, o `RouteDrawer` com o `<Outlet/>` da
// rota `$id` (`spec/24` §1 decisão 5, drawer `SubmissaoKyc` do croqui). Deep link em
// `/admin/kyc/kyc_…` monta a fila embaixo e o drawer aberto; fechar volta para cá
// preservando o filtro.
//
// Sem step-up: `GET /admin/v1/kyc` não está em `ADMIN_STEP_UP_ROUTES`.

import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { adminKycQueueQueryOptions } from '../api/queries/admin-kyc.ts';
import { PageHeader } from '../components/kit/page-header.tsx';
import { RouteDrawer } from '../components/kit/route-drawer.tsx';
import { KycQueue, KycStatusFilter } from '../features/admin-kyc/queue.tsx';
import { useKycDrawerHeader } from '../features/admin-kyc/submission-detail.tsx';

/**
 * A tupla literal é o que o `z.enum` pede; o `tsc` ainda casa isto com `AdminKycStatus`
 * do contrato gerado quando o `status` entra em `adminKycQueueQueryOptions`, então status
 * inventado aqui não compila. Mora na rota-PAI porque o `$id` é filho dela e herda o
 * mesmo `search`.
 */
const searchSchema = z.object({
  status: z.enum(['pending', 'approved', 'rejected', 'draft']).default('pending'),
});

/** Largura do drawer no croqui (`width={480}`). */
const DRAWER_WIDTH = 480;

function AdminKyc() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { status } = search;
  const queue = useInfiniteQuery(adminKycQueueQueryOptions(status));
  const rows = queue.data?.pages.flatMap((page) => page.data) ?? [];

  // A rota `$id` é filha desta: se ela casa, a submissão está aberta por cima da fila.
  const detail = useMatch({ from: '/admin/kyc/$id', shouldThrow: false });
  const header = useKycDrawerHeader(detail?.params.id);

  return (
    <>
      <PageHeader
        title="Fila de KYC"
        sub="Submissões esperando análise, da mais antiga para a mais nova"
        path="/admin/v1/kyc"
        size="lg"
        actions={
          <KycStatusFilter
            value={status}
            onChange={(next) => {
              void navigate({ to: '/admin/kyc', search: { status: next } });
            }}
          />
        }
      />
      <KycQueue
        rows={rows}
        status={status}
        isLoading={queue.isPending}
        error={queue.error}
        onRetry={() => {
          void queue.refetch();
        }}
        hasMore={queue.hasNextPage}
        isLoadingMore={queue.isFetchingNextPage}
        onLoadMore={() => {
          void queue.fetchNextPage();
        }}
        selectedId={detail?.params.id}
        onSelect={(row) => {
          // `search` explícito: o filtro da fila tem de sobreviver à abertura do drawer.
          void navigate({ to: '/admin/kyc/$id', params: { id: row.id }, search });
        }}
      />

      <RouteDrawer
        open={detail !== undefined}
        onClose={() => {
          void navigate({ to: '/admin/kyc', search });
        }}
        title={header.title}
        sub={header.sub}
        width={DRAWER_WIDTH}
      >
        <Outlet />
      </RouteDrawer>
    </>
  );
}

export const Route = createFileRoute('/admin/kyc')({
  validateSearch: searchSchema,
  component: AdminKyc,
});
